import { type CpComparison, cpVerdict, speedDiffPct } from '@felix/domain';
import {
  IconAlertTriangleFilled,
  IconCircleCheckFilled,
  IconCloudFilled,
  IconGasStationFilled,
  IconInfoCircleFilled,
  IconReportAnalyticsFilled,
  IconSettingsFilled,
} from '@tabler/icons-react';
import { Fragment, type ReactNode } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import classes from './NoonReportScreen.module.css';
import { SECTION_ICON, SectionTitle } from './NoonSections.js';
import type { NoonDerived } from './noon-report.js';
import { useNumber } from './numbers.js';

type Rec = Record<string, unknown>;
const rec = (value: unknown): Rec =>
  typeof value === 'object' && value !== null ? (value as Rec) : {};
const list = (value: unknown): Rec[] => (Array.isArray(value) ? (value as Rec[]) : []);
const num = (value: unknown) => (typeof value === 'number' ? value : null);

/** Etiket ve değer: kutu (makine özeti) ya da satır içi (hava özeti; G4-19, G4-22). */
export function Item({
  label,
  value,
  inline = false,
}: {
  label: string;
  value: ReactNode;
  inline?: boolean;
}) {
  return (
    <div className={classes.item} data-inline={inline || undefined}>
      {/* Dar kartta etiket üç noktayla kesilirse tamamı title'da (M6f G7) */}
      <span className={classes.itemLabel} title={label}>
        {label}
      </span>
      <span className={classes.itemValue}>{value}</span>
    </div>
  );
}

/** Ekipman durumu: hep noktalı rozet; çalışmayan nötr (G4-20). */
export function MachineryStatus({ status }: { status: unknown }) {
  const { t } = useTranslation();
  if (typeof status !== 'string') return <>—</>;
  const tone: StatusTone =
    status === 'running' ? 'success' : status === 'standby' ? 'info' : 'neutral';
  return (
    <StatusBadge tone={tone} variant="dot">
      {t(`voyage.noon.machineryStatus.${status}`)}
    </StatusBadge>
  );
}

/** Jeneratörler ayrı ("Generators" ve "Boiler & Others" kutuları; sefer-noon-report.jpeg). */
const isGenerator = (row: Rec) => /^(DG|D\/G|GEN|AE)/i.test(String(row.name ?? ''));

/**
 * Navigation sekmesinin altındaki özet kartlar (sefer-noon-report.jpeg): solda Machinery ve
 * Weather & Sea, sağda Fuel & ROB ve Charter Party Performance (G4-18). Değerler formdan
 * okunur; düzenleme kendi sekmesinde.
 */
export function NoonSummaries({
  derived,
  cp,
  mainEngineFuel,
}: {
  derived: NoonDerived;
  cp: CpComparison | null;
  /** Seferin ana makine yakıtı: ana makinenin tüketimi o yakıtın satırından. */
  mainEngineFuel: string | null;
}) {
  const { t } = useTranslation();
  const n = useNumber();
  const data = rec(useWatch({ name: 'data' }));
  const machinery = rec(data.machinery);
  const engine = rec(machinery.mainEngine);
  const auxiliaries = list(machinery.auxiliaries);
  const generators = auxiliaries.filter(isGenerator);
  const others = auxiliaries.filter((row) => !isGenerator(row));
  const fuel = rec(data.fuel);
  const fuels = list(fuel.fuels);
  const lubes = list(fuel.lubes);
  const water = fuel.freshWater ? rec(fuel.freshWater) : null;
  const weather = rec(data.weather);
  const wind = rec(weather.wind);
  const sea = rec(weather.sea);
  const current = rec(weather.current);
  const m = (key: string, options?: Record<string, unknown>) =>
    t(`voyage.noon.machinery.${key}`, options ?? {});
  const f = (key: string) => t(`voyage.noon.fuel.${key}`);
  const w = (key: string) => t(`voyage.noon.weather.short.${key}`);
  const mt = { digits: 1, fixed: true } as const;
  const deg = (value: number | null) => (value === null ? '—' : `${n(value, 0)}°`);
  const meFuel = mainEngineFuel ? fuels.find((row) => row.fuel === mainEngineFuel) : undefined;

  return (
    <div className={classes.summaryColumns}>
      <div className={classes.summaryColumn}>
        <section className={classes.card} aria-label={`3. ${m('title')}`}>
          <SectionTitle
            icon={<IconSettingsFilled size={SECTION_ICON} />}
            title={`3. ${m('title')}`}
          />
          <div className={classes.machinerySummary}>
            <div className={classes.summaryGroup}>
              <h3>{m('mainEngine')}</h3>
              <div className={classes.miniList}>
                <Item label={m('runningHours')} value={n.unit(num(engine.runningHours), 'h', 0)} />
                <Item label={m('rpm')} value={n(num(engine.rpm), 0)} />
                <Item label={m('loadShort')} value={n.unit(num(engine.loadPct), '%', 0)} />
                <Item label={m('powerShort')} value={n.unit(num(engine.powerKw), 'kW', 0)} />
                {meFuel ? (
                  <Item
                    label={m('fuelConsumption')}
                    value={`${n.unit(num(meFuel.consumption), 'MT', mt)} · ${mainEngineFuel}`}
                  />
                ) : null}
                <Item label={m('status')} value={<MachineryStatus status={engine.status} />} />
              </div>
            </div>
            <div className={classes.summaryGroup}>
              <h3>{m('generators')}</h3>
              {generators.length === 0 ? (
                <p className={classes.muted}>—</p>
              ) : (
                <div className={classes.generatorGrid}>
                  {generators.map((row, index) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: salt okunur özet; satırların kimliği yok
                    <div key={`${String(row.name)}-${index}`} className={classes.miniBox}>
                      <h4>{String(row.name ?? '—')}</h4>
                      <div className={classes.miniList}>
                        <Item
                          label={m('runningHours')}
                          value={n.unit(num(row.runningHours), 'h', 0)}
                        />
                        <Item label={m('loadShort')} value={n.unit(num(row.loadPct), '%', 0)} />
                        {num(row.fuelConsumptionMt) === null ? null : (
                          <Item
                            label={m('fuelConsumption')}
                            value={n.unit(num(row.fuelConsumptionMt), 'MT', mt)}
                          />
                        )}
                        <Item label={m('status')} value={<MachineryStatus status={row.status} />} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className={classes.summaryGroup}>
              <h3>{m('boilerOthers')}</h3>
              {others.length === 0 ? (
                <p className={classes.muted}>—</p>
              ) : (
                <div className={classes.miniList}>
                  {others.map((row, index) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: salt okunur özet; satırların kimliği yok
                    <Fragment key={`${String(row.name)}-${index}`}>
                      <Item
                        label={m('runningHoursOf', { name: String(row.name ?? '—') })}
                        value={
                          num(row.runningHours) === null ? (
                            <MachineryStatus status={row.status} />
                          ) : (
                            n.unit(num(row.runningHours), 'h', 0)
                          )
                        }
                      />
                      {num(row.fuelConsumptionMt) === null ? null : (
                        <Item
                          label={m('fuelConsumption')}
                          value={n.unit(num(row.fuelConsumptionMt), 'MT', mt)}
                        />
                      )}
                    </Fragment>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        <section className={classes.card} aria-label={`5. ${t('voyage.noon.weather.title')}`}>
          <SectionTitle
            icon={<IconCloudFilled size={SECTION_ICON} />}
            title={`5. ${t('voyage.noon.weather.title')}`}
          />
          <div className={classes.weatherSummary}>
            <div className={classes.summaryGroup}>
              <h3>{w('wind')}</h3>
              <Item inline label={w('direction')} value={deg(num(wind.direction))} />
              <Item inline label={w('speed')} value={n.unit(num(wind.speedKn), 'kn', 0)} />
              <Item inline label={w('beaufort')} value={n(num(wind.beaufort), 0)} />
            </div>
            <div className={classes.summaryGroup}>
              <h3>{w('sea')}</h3>
              <Item inline label={w('seaState')} value={n(num(sea.state), 0)} />
              <Item inline label={w('swellDirection')} value={deg(num(sea.swellDirection))} />
              <Item inline label={w('swellHeight')} value={n.unit(num(sea.swellHeightM), 'm', 1)} />
              <Item inline label={w('swellPeriod')} value={n.unit(num(sea.swellPeriodS), 's', 0)} />
            </div>
            <div className={classes.summaryGroup}>
              <h3>{w('weather')}</h3>
              <Item
                inline
                label={w('visibility')}
                value={n.unit(num(weather.visibilityNm), 'nm', 1)}
              />
              <Item inline label={w('airTemp')} value={n.unit(num(weather.airTempC), '°C', 1)} />
              <Item inline label={w('seaTemp')} value={n.unit(num(weather.seaTempC), '°C', 1)} />
              <Item
                inline
                label={w('pressure')}
                value={n.unit(num(weather.pressureHpa), 'hPa', 0)}
              />
            </div>
            <div className={classes.summaryGroup}>
              <h3>{w('current')}</h3>
              <Item inline label={w('direction')} value={deg(num(current.direction))} />
              <Item inline label={w('speed')} value={n.unit(num(current.speedKn), 'kn', 1)} />
            </div>
          </div>
        </section>
      </div>

      <div className={classes.summaryColumn}>
        <section className={classes.card} aria-label={`4. ${f('title')}`}>
          <SectionTitle
            icon={<IconGasStationFilled size={SECTION_ICON} />}
            title={`4. ${f('title')}`}
          />
          <div className={classes.robBox}>
            <table className={classes.robTable}>
              <thead>
                <tr>
                  <th scope="col">{f('fuelType')}</th>
                  <th scope="col">{f('opening')}</th>
                  <th scope="col">{f('consumption')}</th>
                  <th scope="col">{f('bunkering')}</th>
                  <th scope="col">{f('closing')}</th>
                </tr>
              </thead>
              <tbody>
                {fuels.map((row, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: salt okunur özet; satırların kimliği yok, sıra formdaki gibi
                  <tr key={`${String(row.fuel)}-${index}`}>
                    <th scope="row">{String(row.fuel ?? '—')}</th>
                    <td>{n(num(row.opening), mt)}</td>
                    <td>{n(num(row.consumption), mt)}</td>
                    <td>{n(num(row.bunkered), mt)}</td>
                    <td>{n(derived.fuels[index]?.closing ?? null, mt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {lubes.length > 0 ? (
            <div className={classes.robBox}>
              <h3 className={classes.robBoxTitle}>{f('lubes')}</h3>
              <table className={classes.robTable} data-minor>
                <thead className={classes.srOnlyHead}>
                  <tr>
                    <th scope="col">{f('name')}</th>
                    <th scope="col">{f('opening')}</th>
                    <th scope="col">{f('consumption')}</th>
                    <th scope="col">{f('bunkering')}</th>
                    <th scope="col">{f('closing')}</th>
                  </tr>
                </thead>
                <tbody>
                  {lubes.map((row, index) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: salt okunur özet; satırların kimliği yok, sıra formdaki gibi
                    <tr key={`${String(row.name)}-${index}`}>
                      <th scope="row">{String(row.name ?? '—')}</th>
                      <td>{n(num(row.opening), mt)}</td>
                      <td>{n(num(row.consumption), mt)}</td>
                      <td>{n(num(row.bunkered), mt)}</td>
                      <td>{n(derived.lubes[index]?.closing ?? null, mt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {water ? (
            <div className={classes.robBox}>
              <h3 className={classes.robBoxTitle}>{f('freshWater')}</h3>
              <div className={classes.waterRow}>
                <Item label={f('rob')} value={n.unit(num(water.rob), 'MT', mt)} />
                <Item label={f('consumption')} value={n.unit(num(water.consumption), 'MT', mt)} />
                <Item label={f('produced')} value={n.unit(num(water.produced), 'MT', mt)} />
                <Item label={f('bunkered')} value={n.unit(num(water.bunkered), 'MT', mt)} />
                <Item label={f('closing')} value={n.unit(derived.freshWaterClosing, 'MT', mt)} />
              </div>
            </div>
          ) : null}
        </section>

        <CpCard cp={cp} />
      </div>
    </div>
  );
}

const NOTICE_TONE = { within: 'success', outside: 'warning', partial: 'info' } as const;

/**
 * Charter Party Performance kartı: seferin CP değerleriyle bu raporun ortalama hızı ve tüketimi.
 * Biri bilinmiyorsa kısmi (KARAR #96).
 */
export function CpCard({
  cp,
  status = null,
}: {
  cp: CpComparison | null;
  /** CP durumu: son On / Off Hire olayından (ADR 0059 §5). */
  status?: 'on_hire' | 'off_hire' | null;
}) {
  const { t } = useTranslation();
  const verdict = cpVerdict(cp);
  const n = useNumber();
  const k = (key: string) => t(`voyage.noon.cp.${key}`);
  const one = { digits: 1, fixed: true } as const;
  const speedPct = cp ? speedDiffPct(cp.plannedSpeedKn, cp.speedDiffKn) : null;
  const diff = (value: number | null, unit: string, goodWhenPositive: boolean) =>
    value === null ? null : (
      <StatusBadge tone={value >= 0 === goodWhenPositive ? 'success' : 'danger'}>
        {`${value > 0 ? '+' : ''}${n.unit(value, unit, one)}`}
      </StatusBadge>
    );
  return (
    <section className={classes.card} aria-label={k('title')}>
      <SectionTitle icon={<IconReportAnalyticsFilled size={SECTION_ICON} />} title={k('title')} />
      {cp ? (
        <>
          <dl className={classes.cpGrid}>
            <div>
              <dt className={classes.itemLabel}>{k('status')}</dt>
              <dd>
                <strong>{status ? k(`statuses.${status}`) : '—'}</strong>
              </dd>
            </div>
            <div>
              <dt className={classes.itemLabel}>{k('plannedSpeed')}</dt>
              <dd>
                <strong>{n.unit(cp.plannedSpeedKn, 'kn', one)}</strong>
              </dd>
            </div>
            <div>
              <dt className={classes.itemLabel}>{k('actualSpeed')}</dt>
              <dd>
                <strong>{n.unit(cp.actualSpeedKn, 'kn', one)}</strong>
                {diff(cp.speedDiffKn, 'kn', true)}
                {/* Günlük performans yüzdesi (g3 "+1.02 kt (+8.5%)") */}
                {speedPct === null ? null : (
                  <span
                    className={classes.muted}
                  >{`${speedPct > 0 ? '+' : ''}${n(speedPct, one)} %`}</span>
                )}
              </dd>
            </div>
            <div>
              <dt className={classes.itemLabel}>{k('plannedConsumption')}</dt>
              <dd>
                <strong>{n.unit(cp.plannedConsumptionMt, 'MT/day', one)}</strong>
              </dd>
            </div>
            <div>
              <dt className={classes.itemLabel}>{k('actualConsumption')}</dt>
              <dd>
                <strong>{n.unit(cp.actualConsumptionMt, 'MT/day', one)}</strong>
                {diff(cp.consumptionDiffMt, 'MT', false)}
              </dd>
            </div>
          </dl>
          {verdict.status === 'unknown' ? null : (
            <div className={classes.notice} data-tone={NOTICE_TONE[verdict.status]}>
              {verdict.status === 'within' ? (
                <IconCircleCheckFilled size={16} aria-hidden />
              ) : verdict.status === 'partial' ? (
                <IconInfoCircleFilled size={16} aria-hidden />
              ) : (
                <IconAlertTriangleFilled size={16} aria-hidden />
              )}
              <span>{k(verdict.status)}</span>
            </div>
          )}
        </>
      ) : (
        <p className={classes.muted}>{k('unknown')}</p>
      )}
    </section>
  );
}
